import { OrderedMap, OrderedSet } from 'immutable';
import { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs';

import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import { addMultipleAction } from 'entities/partners/actions';
import PartnersApi from 'entities/partners/api';
import { PARTNER_STATUSES } from 'entities/partners/constants';
import { selectUser } from 'entities/user/selectors';

import {
  respondSuccess,
  respondFailed,
  APPROVE,
  DENY,
  ApproveAction,
  DenyAction,
  SET_FETCH_PARAMS,
  FetchParamsAction,
  fetchSuccessAction,
  fetchFailedAction,
  setFetchParamsAction,
  RespondSuccess,
  setDisplayedPartnerIdsAction,
  setTotalsAction,
  CREATE_PARTNER,
  createPartnerSuccessAction,
  createPartnerFailedAction,
} from './types';
import { selectFetchParams } from './selectors';

const fetch = (action$: ActionsObservable<FetchParamsAction | RespondSuccess>, store: any) =>
  action$.ofType(SET_FETCH_PARAMS).mergeMap(() => {
    const state = store.getState();
    const user = selectUser(state);
    const isNonprofit = user?.organization?.isNonprofit();
    const fetchParams = selectFetchParams(state);
    const pageNum = fetchParams.get('pageNum') || 0;
    const limit = fetchParams.get('limit');
    const skip = limit * pageNum;
    const partnerStatus: PARTNER_STATUSES = fetchParams.get('partnerStatus');
    const sort = fetchParams.get('sort');
    const order = fetchParams.get('order');
    const search = fetchParams.get('searchTerm');

    const status =
      isNonprofit && partnerStatus === PARTNER_STATUSES.rejected
        ? [partnerStatus, PARTNER_STATUSES.inactive]
        : [partnerStatus];

    return PartnersApi.fetchRaw({
      status,
      limit,
      skip,
      sort,
      order,
      search,
    })
      .mergeMap((resultingAction) => [
        setTotalsAction({
          count: resultingAction.total,
        }),
        addMultipleAction(
          OrderedMap(resultingAction.partners.map((partner) => [partner.id, partner]))
        ),
        setDisplayedPartnerIdsAction(
          OrderedSet(resultingAction.partners.map((partner) => partner.id))
        ),
        fetchSuccessAction(),
      ])
      .takeUntil(action$.ofType(SET_FETCH_PARAMS))
      .catch((e) => [fetchFailedAction(e), showErrorAction('Failed to load affiliations')]);
  });

const approve = (action$: ActionsObservable<ApproveAction>) =>
  action$.ofType(APPROVE).mergeMap(({ id }) =>
    PartnersApi.update(id, { status: PARTNER_STATUSES.accepted })
      .mergeMap((resultingAction) => [
        resultingAction,
        respondSuccess(id),
        showMessageAction('Affiliation approved!'),
        setFetchParamsAction({}),
      ])
      .catch(() => [respondFailed(id), showErrorAction('Failed to approve affiliate')])
  );

const deny = (action$: ActionsObservable<DenyAction>) =>
  action$.ofType(DENY).mergeMap(({ id }) =>
    PartnersApi.update(id, { status: PARTNER_STATUSES.rejected })
      .mergeMap((resultingAction) => [
        resultingAction,
        respondSuccess(id),
        showMessageAction('Affiliation denied'),
        setFetchParamsAction({}),
      ])
      .catch(() => [respondFailed(id), showErrorAction('Failed to deny affiliation')])
  );

const createPartner = (action$: any) =>
  action$.ofType(CREATE_PARTNER).mergeMap(({ id }: { id: any }) =>
    Observable.from(PartnersApi.create(id))
      .mergeMap((action) => [
        action,
        createPartnerSuccessAction(),
        showMessageAction('Affiliation saved'),
        setFetchParamsAction({}),
      ])
      .catch((e) =>
        Observable.of(
          createPartnerFailedAction(e),
          showErrorAction(e?.responseBody?.errors?.[0]?.message || 'Failed to save affiliation')
        )
      )
  );

export default [approve, createPartner, deny, fetch];
